import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { FaEye, FaEdit, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Select from 'react-select';
import GSTVerificationModal from '../../components/GSTVerificationModal';
import PANVerificationModal from '../../components/PANVerificationModal';
import useDraftManager from '../../hooks/useDraftManager';
import { toast } from 'react-toastify';
import StationSelect from '../../components/common/StationSelect';
import MasterSelection from '../../components/MasterSelection';

// States list for station creation
const INDIAN_STATES = [
  'Andaman and Nicobar Islands',
  'Andhra Pradesh',
  'Arunachal Pradesh',
  'Assam',
  'Bihar',
  'Chandigarh',
  'Chhattisgarh',
  'Dadra and Nagar Haveli',
  'Daman and Diu',
  'Delhi',
  'Goa',
  'Gujarat',
  'Haryana',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Jharkhand',
  'Karnataka',
  'Kerala',
  'Ladakh',
  'Lakshadweep',
  'Madhya Pradesh',
  'Maharashtra',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Odisha',
  'Puducherry',
  'Punjab',
  'Rajasthan',
  'Sikkim',
  'Tamil Nadu',
  'Telangana',
  'Tripura',
  'Uttar Pradesh',
  'Uttarakhand',
  'West Bengal',
  'Bhutan',
  'Nepal',
  'Bangladesh'
];

// Clean form data utility function
const cleanFormData = (data, isEditing = false) => {
  const cleanedData = {};
  
  // Required fields (always include)
  const requiredFields = ['name', 'phone', 'station', 'createdBy'];
  requiredFields.forEach(field => {
    cleanedData[field] = data[field];
  });

  // Optional fields (only include if they have values, or send empty string when editing to allow clearing)
  const optionalFields = ['secondaryPhone', 'email', 'pinCode', 'panNo', 'gstNo', 'siteAddress', 'deliveryAddress', 'salutation'];
  optionalFields.forEach(field => {
    if (data[field]?.trim()) {
      cleanedData[field] = data[field].trim();
    } else if (isEditing) {
      cleanedData[field] = '';
    }
  });
  
  // Handle MA field
  if (data.ma && data.ma.maType && data.ma.maType !== '' && data.ma.maId && data.ma.maId !== '') {
    // Valid MA data - include it
    cleanedData.ma = {
      maType: data.ma.maType,
      maId: data.ma.maId
    };
  } else if (isEditing) {
    // Editing and MA is empty - send empty object to trigger removal in backend
    cleanedData.ma = {
      maType: '',
      maId: ''
    };
  }
  // If creating new customer and MA is empty, don't include it at all
  
  return cleanedData;
};

// Custom styles for react-select
const customSelectStyles = (darkMode) => ({
  control: (provided, state) => ({
    ...provided,
    minHeight: '42px',
    backgroundColor: darkMode ? '#4b5563' : 'white',
    border: `1px solid ${darkMode ? '#6b7280' : '#d1d5db'}`,
    borderRadius: '0.5rem',
    fontSize: '14px',
    boxShadow: state.isFocused ? `0 0 0 3px ${darkMode ? 'rgba(96, 165, 250, 0.2)' : 'rgba(59, 130, 246, 0.1)'}` : 'none',
    borderColor: state.isFocused ? (darkMode ? '#60a5fa' : '#3b82f6') : (darkMode ? '#6b7280' : '#d1d5db'),
    '&:hover': {
      borderColor: state.isFocused ? (darkMode ? '#60a5fa' : '#3b82f6') : (darkMode ? '#9ca3af' : '#9ca3af')
    },
    transition: 'all 0.3s'
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? (darkMode ? '#3b82f6' : '#3b82f6') 
      : state.isFocused 
        ? (darkMode ? '#374151' : '#eff6ff') 
        : (darkMode ? '#1f2937' : 'white'),
    color: state.isSelected ? 'white' : (darkMode ? '#f3f4f6' : '#374151'),
    fontSize: '14px',
    padding: '8px 12px',
    cursor: 'pointer'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: darkMode ? '#f3f4f6' : '#374151',
    fontSize: '14px'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: darkMode ? '#9ca3af' : '#9ca3af',
    fontSize: '14px'
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: darkMode ? '#1f2937' : 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
    zIndex: 9999
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '4px',
    borderRadius: '0.5rem',
    backgroundColor: darkMode ? '#1f2937' : 'white'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: state.isFocused ? (darkMode ? '#60a5fa' : '#3b82f6') : (darkMode ? '#9ca3af' : '#9ca3af'),
    '&:hover': {
      color: darkMode ? '#60a5fa' : '#3b82f6'
    }
  }),
  input: (provided) => ({
    ...provided,
    color: darkMode ? '#f3f4f6' : '#374151'
  })
});

// Options for select fields
const salutationOptions = [
  { value: '', label: 'Select Salutation' },
  { value: 'Mr.', label: 'Mr.' },
  { value: 'Mrs.', label: 'Mrs.' },
  { value: 'M/s.', label: 'M/s.' },
  { value: 'Miss.', label: 'Miss.' },
  { value: 'Dr.', label: 'Dr.' },
  { value: 'Prof.', label: 'Prof.' }
];

const pageSizeOptions = [
  { value: 5, label: '5 per page' },
  { value: 10, label: '10 per page' },
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' }
];

const CustomerManagement = () => {
  const { user } = useContext(AuthContext);
  
  // Reference for name input to focus after edit
  const nameInputRef = useRef(null);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const [customers, setCustomers] = useState([]);
  const [stations, setStations] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [references, setReferences] = useState([]);
  const [mas, setMas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalCustomers, setTotalCustomers] = useState(0);

  // Toast notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  // Modal state
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    secondaryPhone: '',
    email: '',
    station: '',
    pinCode: '',
    panNo: '',
    gstNo: '',
    siteAddress: '',
    deliveryAddress: '',
    salutation: '',
    ma: {
      maType: '',
      maId: '',
      maName: ''
    },
    createdBy: user?.user?._id
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state
  const [isPanReadOnly, setIsPanReadOnly] = useState(false); // To make PAN read-only when extracted from GST
  const [isGstReadOnly, setIsGstReadOnly] = useState(false); // To make GST read-only when verified
  const [showGstPanFields, setShowGstPanFields] = useState(false); // To show GST/PAN fields only after verification
  const [duplicateErrors, setDuplicateErrors] = useState({}); // Track duplicate field errors

  // Phone duplicate info (not an error, just info)
  const [phoneDuplicateInfo, setPhoneDuplicateInfo] = useState(null);

  // Phone duplicate confirmation states
  const [showPhoneDuplicateModal, setShowPhoneDuplicateModal] = useState(false);
  const [duplicatePhoneCustomers, setDuplicatePhoneCustomers] = useState([]);
  const [pendingSubmission, setPendingSubmission] = useState(false);

  // Station creation states
  const [showCreateStation, setShowCreateStation] = useState(false);
  const [stationForm, setStationForm] = useState({
    name: '',
    state: '',
    createdBy: user?.user?._id
  });
  const [isCreatingStation, setIsCreatingStation] = useState(false);

  // GST Verification Modal state
  const [showGSTVerification, setShowGSTVerification] = useState(false);
  
  // PAN Verification Modal state
  const [showPANVerification, setShowPANVerification] = useState(false);

  // MasterSelection modal state for MA selection
  const [showMasterSelection, setShowMasterSelection] = useState(false);

  // Initialize draft manager
  const draftManager = useDraftManager({
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    featureType: 'customerManagement',
    formData: form,
    setFormData: setForm,
    autoDraft: true, // Enable auto-save
    autoDraftInterval: 5000, // 5 seconds
    onError: (error) => {
      console.error('Draft error:', error);
    },
    onDraftSaved: () => {
      setNotificationMessage('Draft saved automatically');
      setShowNotification(true);
    },
    onDraftLoaded: (loadedData) => {
      console.log('Draft loaded:', loadedData);
      Swal.fire({
        icon: 'success',
        title: 'Draft Loaded',
        text: 'Your previous draft has been loaded',
        timer: 2000,
        showConfirmButton: false
      });
    }
  });

  // GST validation and PAN extraction function
  const validateGSTAndExtractPAN = async (gstValue) => {
    if (!gstValue || gstValue.length !== 15) {
      return { isValid: false, error: 'GST number must be 15 characters long' };
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/customers/validate-gst`, {
        gstNo: gstValue
      });
      
      if (response.data.isValid) {
        return { isValid: true, panNo: response.data.panNo };
      }
    } catch (error) {
      return { isValid: false, error: error.response?.data?.error || 'Invalid GST number' };
    }
  };

  // Handle GST input change with validation and PAN extraction
  const handleGSTChange = async (e) => {
    const gstValue = e.target.value.toUpperCase();
    setForm({ ...form, gstNo: gstValue });

    if (gstValue.length === 15) {
      const result = await validateGSTAndExtractPAN(gstValue);
      if (result.isValid) {
        setForm(prev => ({ ...prev, gstNo: gstValue, panNo: result.panNo }));
        setIsPanReadOnly(true);
        
        // Check for GST duplicate
        await checkFieldDuplicate('gstNo', gstValue);
        
        if (!duplicateErrors.gstNo) {
          Swal.fire({
            icon: 'success',
            title: 'Valid GST',
            text: `PAN ${result.panNo} extracted from GST`,
            timer: 2000,
            showConfirmButton: false
          });
        }
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Invalid GST',
          text: result.error
        });
        setIsPanReadOnly(false);
      }
    } else {
      setIsPanReadOnly(false);
      if (gstValue.trim()) {
        await checkFieldDuplicate('gstNo', gstValue);
      }
    }
  };

  // Handle clearing GST and PAN
  const handleClearGST = () => {
    setForm({ ...form, gstNo: '', panNo: '' });
    setIsPanReadOnly(false);
    setIsGstReadOnly(false);
    setShowGstPanFields(false); // Hide fields again when cleared
  };

  // Handle PAN input change
  const handlePANChange = async (e) => {
    const panValue = e.target.value.toUpperCase();
    setForm({ ...form, panNo: panValue });
    
    // Check for PAN duplicate on blur-like behavior
    if (panValue.length === 10) {
      await checkFieldDuplicate('panNo', panValue);
    } else if (panValue.trim()) {
      // Check even for partial PAN if user stops typing
      setTimeout(async () => {
        if (form.panNo === panValue && panValue.trim()) {
          await checkFieldDuplicate('panNo', panValue);
        }
      }, 500);
    }
  };

  // Handle GST verification modal data
  const handleGSTVerificationData = ({ gstData, gstin, useData }) => {
    console.log('GST Verification Data Received:', { gstData, gstin, useData });
    if (useData && gstData) {
      const extractedData = gstData.extractedData || {};
      console.log('Extracted Data:', extractedData);
      
      // Extract PAN from GSTIN (characters 3-12, 0-indexed as 2-12)
      const extractedPAN = gstin.substring(2, 12);
      
      // Update form with comprehensive verified GST data
      setForm(prevForm => ({
        ...prevForm,
        gstNo: gstin,
        panNo: extractedPAN, // Auto-set PAN from GSTIN
        name: extractedData.legalName || extractedData.tradeName || prevForm.name,
        siteAddress: extractedData.address || prevForm.siteAddress,
        // Only update delivery address if it's empty
        deliveryAddress: !prevForm.deliveryAddress ? extractedData.address : prevForm.deliveryAddress
      }));
      
      setIsPanReadOnly(true);
      setIsGstReadOnly(true);
      setShowGstPanFields(true); // Show GST/PAN fields after successful verification
      
      // Show detailed success message with populated data
      const populatedFields = [];
      if (extractedData.legalName || extractedData.tradeName) populatedFields.push('Name');
      if (extractedPAN) populatedFields.push('PAN');
      if (extractedData.address) populatedFields.push('Address');
      
      Swal.fire({
        title: 'GST Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>GSTIN:</strong> ${gstin}</p>
            <p class="mb-2"><strong>Extracted PAN:</strong> ${extractedPAN}</p>
            <p class="mb-2"><strong>Status:</strong> ${gstData.sts || 'N/A'}</p>
            <p class="mb-2"><strong>Constitution:</strong> ${gstData.ctb || 'N/A'}</p>
            <p class="mb-2"><strong>Registration Date:</strong> ${gstData.rgdt || 'N/A'}</p>
            ${populatedFields.length > 0 ? `<p class="mt-3 text-sm text-green-600"><strong>Auto-filled:</strong> ${populatedFields.join(', ')}</p>` : ''}
            <p class="mt-3 text-sm text-blue-600">ℹ️ PAN has been automatically extracted from GSTIN. No separate PAN verification needed.</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3b82f6'
      });
    }
  };
  
  // Handle PAN verification modal data
  const handlePANVerificationData = ({ panNo, verificationData }) => {
    console.log('PAN Verification Data Received:', { panNo, verificationData });
    
    // Update form with verified PAN
    setForm(prevForm => ({
      ...prevForm,
      panNo: panNo
    }));
    
    // Make PAN field read-only after verification
    setIsPanReadOnly(true);
    
    // Show GST/PAN fields after PAN verification
    setShowGstPanFields(true);
    
    // Check if PAN has linked GSTINs
    const gstinList = verificationData.gstinResList || [];
    
    if (gstinList.length > 0) {
      // PAN has linked GSTINs - ask user to select one
      const gstinOptions = gstinList.map(item => 
        `<option value="${item.gstin}">${item.gstin} (${item.authStatus || 'N/A'} - State: ${item.stateCd || 'N/A'})</option>`
      ).join('');
      
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-3"><strong>PAN:</strong> ${panNo}</p>
            <p class="mb-2 font-semibold">This PAN has ${gstinList.length} linked GSTIN(s):</p>
            <select id="gstin-select" class="w-full border border-gray-300 rounded-md p-2 mb-3">
              <option value="">-- Select a GSTIN --</option>
              ${gstinOptions}
            </select>
            <p class="text-sm text-blue-600">💡 Select a GSTIN to auto-fill GST field (no re-verification needed)</p>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Use Selected GSTIN',
        cancelButtonText: 'Skip',
        confirmButtonColor: '#3b82f6',
        cancelButtonColor: '#6b7280',
        preConfirm: () => {
          const selectedGSTIN = document.getElementById('gstin-select').value;
          if (!selectedGSTIN) {
            Swal.showValidationMessage('Please select a GSTIN');
            return false;
          }
          return selectedGSTIN;
        }
      }).then((result) => {
        if (result.isConfirmed && result.value) {
          // User selected a GSTIN - set it directly without verification
          setForm(prevForm => ({
            ...prevForm,
            gstNo: result.value
          }));
          // Make GST field read-only as well
          setIsGstReadOnly(true);
          
          Swal.fire({
            icon: 'success',
            title: 'GSTIN Set Successfully!',
            html: `
              <div class="text-left">
                <p class="mb-2"><strong>Selected GSTIN:</strong> ${result.value}</p>
                <p class="text-sm text-green-600 mt-3">✓ GSTIN has been set from PAN verification. No separate verification needed.</p>
                <p class="text-sm text-gray-600 mt-2">ℹ️ Both PAN and GST fields are now locked.</p>
              </div>
            `,
            timer: 3000,
            showConfirmButton: false
          });
        }
      });
    } else {
      // PAN verified but no linked GSTINs
      Swal.fire({
        icon: 'success',
        title: 'PAN Verified Successfully!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>PAN:</strong> ${panNo}</p>
            <p class="mb-2"><strong>Status:</strong> ${verificationData.authStatus || 'Active'}</p>
            ${verificationData.stateCd ? `<p class="mb-2"><strong>State Code:</strong> ${verificationData.stateCd}</p>` : ''}
            <p class="text-sm text-yellow-600 mt-3">⚠️ No linked GSTINs found for this PAN.</p>
            <p class="text-sm text-gray-600">ℹ️ PAN field is now locked.</p>
          </div>
        `,
        timer: 3000,
        showConfirmButton: false
      });
    }
  };

  // Open GST verification modal
  const openGSTVerification = () => {
    console.log('Opening GST verification modal');
    setShowGSTVerification(true);
  };

  useEffect(() => {
    fetchCustomers(currentPage);
    fetchStations();
    fetchDealers();
    fetchReferences();
    fetchMAs();
  }, [currentPage, pageSize, searchQuery]);

  const fetchCustomers = async (page = 1) => {
    try {
      const companyId = user.user.company._id || user.user.company;
      console.log('Frontend - fetchCustomers - companyId:', companyId);
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/customers?page=${page}&limit=${pageSize}&filter=${searchQuery}&userCompanyId=${companyId}`
      );
      setCustomers(response.data.customers);
      setTotalPages(response.data.totalPages);
      setTotalCustomers(response.data.totalCustomers || 0);
    } catch (error) {
      console.error('Error fetching customers:', error);
      Swal.fire('Error', 'Failed to fetch customers', 'error');
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      console.log('Fetched stations:', response.data);
      setStations(response.data);
    } catch (error) {
      console.error('Error fetching stations:', error);
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  const fetchDealers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/dealers/all?userCompanyId=${user?.user?.company}`);
      console.log('Fetched dealers:', response.data);
      setDealers(response.data.dealers || []);
    } catch (error) {
      console.error('Error fetching dealers:', error);
    }
  };

  const fetchReferences = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/references/all?userCompanyId=${user?.user?.company}`);
      console.log('Fetched references:', response.data);
      setReferences(response.data || []);
    } catch (error) {
      console.error('Error fetching references:', error);
    }
  };

  const fetchMAs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/mas/all?userCompanyId=${user?.user?.company}`);
      console.log('Fetched MAs:', response.data);
      setMas(response.data || []);
    } catch (error) {
      console.error('Error fetching MAs:', error);
    }
  };

  // Station creation functions
  const resetStationForm = () => {
    setStationForm({
      name: '',
      state: '',
      createdBy: user?.user?._id
    });
  };

  const handleStationFormChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    
    // Convert station name to uppercase
    if (name === 'name') {
      processedValue = value.toUpperCase();
    }
    
    setStationForm(prev => ({ ...prev, [name]: processedValue }));
  };

  const validateStationForm = () => {
    const errors = [];
    
    if (!stationForm.name?.trim()) {
      errors.push('Station name is required');
    }
    if (!stationForm.state?.trim()) {
      errors.push('State selection is required');
    }
    
    // Check for duplicate station name
    const duplicate = stations.find(
      station => station.name.toLowerCase() === stationForm.name.toLowerCase()
    );
    if (duplicate) {
      errors.push('A station with this name already exists');
    }
    
    return errors;
  };

  const handleCreateStation = async () => {
    const errors = validateStationForm();
    if (errors.length > 0) {
      Swal.fire('Validation Error', errors.join('\n'), 'error');
      return;
    }

    setIsCreatingStation(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/stations`, {
        name: stationForm.name,
        state: stationForm.state,
        createdBy: stationForm.createdBy
      });
      
      const newStation = response.data.station;
      if (newStation) {
        // Refresh stations list from backend to ensure sync
        await fetchStations();
        
        // Select the newly created station
        setForm(prev => ({ ...prev, station: newStation._id }));
        
        // Reset station form and close modal
        resetStationForm();
        setShowCreateStation(false);
        
        // Show success message
        Swal.fire('Success', `Station "${newStation.name}" created successfully!`, 'success');
      }
    } catch (error) {
      console.error('Error creating station:', error);
      
      let errorMessage = 'Failed to create station';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    } finally {
      setIsCreatingStation(false);
    }
  };

  // Check for duplicate values
  const checkForDuplicates = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/customers/validate-unique`,
        {
          phone: form.phone?.trim(),
          email: form.email?.trim(),
          gstNo: form.gstNo?.trim(),
          panNo: form.panNo?.trim(),
          customerId: isEditing ? editingId : null
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return { hasDuplicates: false, duplicates: {} };
    }
  };

  // Check individual field for duplicates
  const checkFieldDuplicate = async (fieldName, value) => {
    if (!value?.trim()) {
      setDuplicateErrors(prev => ({ ...prev, [fieldName]: null }));
      if (fieldName === 'phone') setPhoneDuplicateInfo(null);
      return;
    }

    try {
      const checkData = { customerId: isEditing ? editingId : null };
      checkData[fieldName] = value.trim();
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/customers/validate-unique`,
        checkData
      );
      
      if (response.data.hasDuplicates && response.data.duplicates[fieldName]) {
        // For phone, we don't show error - we'll show confirmation modal on submit
        if (fieldName === 'phone') {
          setDuplicateErrors(prev => ({ ...prev, [fieldName]: null }));
          const count = response.data.duplicates.phone.count;
          setPhoneDuplicateInfo(`This phone number is used by ${count} existing customer${count > 1 ? 's' : ''}. You can still proceed.`);
        } else {
          // For other fields (email, GST, PAN), show error
          setDuplicateErrors(prev => ({ 
            ...prev, 
            [fieldName]: response.data.duplicates[fieldName].message 
          }));
        }
      } else {
        setDuplicateErrors(prev => ({ ...prev, [fieldName]: null }));
        if (fieldName === 'phone') setPhoneDuplicateInfo(null);
      }
    } catch (error) {
      console.error(`Error checking ${fieldName} duplicate:`, error);
    }
  };

  // Enhanced validation with detailed error messages
  const validateForm = () => {
    // Required fields validation
    if (!form.name || !form.phone || !form.station || !form.salutation) {
      Swal.fire({
        icon: 'error',
        title: 'Required Fields Missing',
        text: 'Name, Salutation, Phone, and Station are required fields.'
      });
      return false;
    }

    // Phone validation
    if (!/^\d{10}$/.test(form.phone)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Phone Number',
        text: 'Primary phone number must be exactly 10 digits.'
      });
      return false;
    }

    // Secondary phone validation (if provided)
    if (form.secondaryPhone && !/^\d{10}$/.test(form.secondaryPhone)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Secondary Phone',
        text: 'Secondary phone number must be exactly 10 digits.'
      });
      return false;
    }

    // Email validation (if provided)
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Email',
        text: 'Please enter a valid email address.'
      });
      return false;
    }

    // GST validation (if provided)
    if (form.gstNo && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/.test(form.gstNo)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid GSTIN',
        text: 'Please enter a valid GSTIN number.'
      });
      return false;
    }

    // PAN validation (if provided)
    if (form.panNo && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNo)) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid PAN',
        text: 'Please enter a valid PAN number (e.g., ABCDE1234F).'
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (skipPhoneCheck = false) => {
    // If already submitting, don't allow another submission
    if (isSubmitting) return;
    
    if (!validateForm()) return;

    try {
      setIsSubmitting(true); // Start loading state
      
      // Check for duplicates first
      const duplicateCheck = await checkForDuplicates();
      
      // Handle phone duplicates separately - show confirmation modal
      if (duplicateCheck.hasDuplicates && duplicateCheck.duplicates.phone && !skipPhoneCheck) {
        setDuplicatePhoneCustomers(duplicateCheck.duplicates.phone.customers);
        setShowPhoneDuplicateModal(true);
        setPendingSubmission(true);
        setIsSubmitting(false);
        return;
      }
      
      // Handle other duplicates (email, GST, PAN) - block submission
      if (duplicateCheck.hasDuplicates) {
        const otherDuplicates = Object.entries(duplicateCheck.duplicates)
          .filter(([field]) => field !== 'phone')
          .map(([field, data]) => `• ${data.message}`)
          .join('\n');
        
        if (otherDuplicates) {
          Swal.fire({
            icon: 'error',
            title: 'Duplicate Values Found',
            text: `The following duplicates were found:\n\n${otherDuplicates}`,
            confirmButtonColor: '#3b82f6'
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      Swal.fire({
        title: `${isEditing ? 'Updating' : 'Adding'} Customer...`,
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false
      });

      const cleanedFormData = cleanFormData(form, isEditing);
      
      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/customers/${editingId}`,
          {
            ...cleanedFormData,
            updatedBy: user?.user?._id,
          }
        );
        Swal.fire('Success!', 'Customer updated successfully!', 'success');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/customers`,
          cleanedFormData
        );
        Swal.fire('Success!', 'Customer added successfully!', 'success');
      }
      
      // Delete the draft after successful customer creation/update
      if (draftManager.currentDraftId) {
        try {
          await draftManager.deleteDraft(draftManager.currentDraftId);
          console.log('✅ Draft deleted after successful customer save');
        } catch (draftError) {
          console.error('⚠️ Failed to delete draft:', draftError);
          // Don't show error to user, draft deletion is not critical
        }
      }
      
      fetchCustomers(currentPage);
      resetForm();
      setPendingSubmission(false);
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to save customer.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMsg
      });
    } finally {
      setIsSubmitting(false); // End loading state regardless of outcome
    }
  };

  // Handle phone duplicate confirmation
  const handlePhoneDuplicateConfirm = () => {
    setShowPhoneDuplicateModal(false);
    handleSubmit(true); // Skip phone check and proceed with submission
  };

  // Handle phone duplicate cancellation
  const handlePhoneDuplicateCancel = () => {
    setShowPhoneDuplicateModal(false);
    setPendingSubmission(false);
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setForm({
      name: '',
      phone: '',
      secondaryPhone: '',
      email: '',
      station: '',
      pinCode: '',
      panNo: '',
      gstNo: '',
      siteAddress: '',
      deliveryAddress: '',
      salutation: '',
      ma: {
        maType: '',
        maId: '',
        maName: ''
      },
      createdBy: user?.user?._id
    });
    setIsEditing(false);
    setEditingId(null);
    setIsSubmitting(false); // Ensure submission state is reset
    setIsPanReadOnly(false); // Reset PAN read-only state
    setIsGstReadOnly(false); // Reset GST read-only state
    setShowGstPanFields(false); // Hide GST/PAN fields on form reset
    setDuplicateErrors({}); // Clear all duplicate errors
    setPhoneDuplicateInfo(null); // Clear phone duplicate info
  };

  const startEditing = async (customer) => {
    // Delete any existing draft when editing a customer
    if (draftManager.currentDraftId) {
      try {
        await draftManager.deleteDraft(draftManager.currentDraftId);
        console.log('✅ Draft deleted when editing customer');
      } catch (draftError) {
        console.error('⚠️ Failed to delete draft on edit:', draftError);
        // Continue anyway, draft deletion is not critical
      }
    }

    setIsEditing(true);
    setEditingId(customer._id);
    
    const formData = {
      name: customer.name || '',
      phone: customer.phone || '',
      station: customer.station?._id || '',
      createdBy: customer.createdBy || user?.user?._id,
    };

    // Only add optional fields if they have values
    formData.secondaryPhone = customer.secondaryPhone?.trim() || '';
    formData.email = customer.email?.trim() || '';
    formData.pinCode = customer.pinCode?.trim() || '';
    formData.panNo = customer.panNo?.trim() || '';
    formData.gstNo = customer.gstNo?.trim() || '';
    formData.siteAddress = customer.siteAddress?.trim() || '';
    formData.deliveryAddress = customer.deliveryAddress?.trim() || '';
    formData.salutation = customer.salutation?.trim() || '';
    
    // Handle MA field
    const maObj = customer.ma?.maId;
    let maName = '';
    if (maObj) {
      if (customer.ma.maType === 'Dealer') {
        maName = maObj.businessName || maObj.name || '';
      } else {
        maName = maObj.name || maObj.guestName || '';
      }
    }
    formData.ma = {
      maType: customer.ma?.maType || '',
      maId: customer.ma?.maId?._id || '',
      maName: maName
    };
    
    setForm(formData);
    
    // Check if PAN should be read-only (if GST exists and PAN matches extracted PAN)
    if (customer.gstNo?.trim() && customer.gstNo.length === 15 && customer.panNo?.trim()) {
      const extractedPAN = customer.gstNo.substring(2, 12);
      if (customer.panNo === extractedPAN) {
        setIsPanReadOnly(true);
        setIsGstReadOnly(true); // Also set GST as read-only for verified data
        setShowGstPanFields(true); // Show fields for verified data
      } else {
        setIsPanReadOnly(false);
        setIsGstReadOnly(false);
        setShowGstPanFields(customer.gstNo?.trim() ? true : false); // Show if GST exists
      }
    } else {
      setIsPanReadOnly(false);
      setIsGstReadOnly(false);
      setShowGstPanFields(customer.gstNo?.trim() ? true : false); // Show if GST exists
    }
    
    // Focus on name input after state updates
    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 0);
  };

  const viewCustomerDetails = (customer) => {
    setSelectedCustomer(customer);
  };

  const closeViewCustomerModal = () => {
    setSelectedCustomer(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <div className={`p-3 sm:p-4 md:p-6 shadow-xl rounded-xl md:rounded-2xl transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
      {/* Header */}
      <div className="mb-4 sm:mb-6 md:mb-8">
        <div className={`flex items-center justify-between p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl transition-colors duration-300 ${
          darkMode 
            ? 'bg-gradient-to-r from-green-900 via-teal-900 to-blue-900' 
            : 'bg-gradient-to-r from-green-600 via-teal-600 to-blue-600'
        }`}>
          <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-4">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm p-2 sm:p-2.5 md:p-3 rounded-lg">
              <svg className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                Customer Management
              </h2>
              <p className="text-green-100 text-xs sm:text-sm mt-0.5 sm:mt-1">Manage your customer relationships</p>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm px-4 py-2 rounded-lg">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-white font-medium">{isEditing ? 'Edit Mode' : 'Create Mode'}</span>
          </div>
        </div>
      </div>

      {/* -------------------------
          ADD / EDIT CUSTOMER SECTION
         ------------------------- */}
      <div className={`mb-4 sm:mb-5 md:mb-6 p-4 sm:p-5 md:p-6 rounded-lg md:rounded-xl border transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-gradient-to-br from-gray-50 to-white border-gray-200 shadow-md'
      }`}>
        <h3 className={`text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 flex items-center transition-colors duration-300 ${
          darkMode ? 'text-gray-100' : 'text-gray-800'
        }`}>
          <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {isEditing ? 'Edit Customer' : 'Add New Customer'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* name */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              ref={nameInputRef}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
              placeholder="Enter customer name"
              className={`border rounded-lg p-2.5 w-full uppercase transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
              }`}
              style={{ textTransform: 'uppercase' }}
            />
          </div>
            {/* salutation */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Salutation <span className="text-red-500">*</span>
            </label>
            <Select
              name="salutation"
              value={salutationOptions.find(option => option.value === form.salutation) || null}
              onChange={(selectedOption) => setForm({ ...form, salutation: selectedOption?.value || '' })}
              options={salutationOptions}
              styles={customSelectStyles(darkMode)}
              isClearable
              placeholder="Select Salutation"
            />
          </div>
          
          {/* phone */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Phone (Primary) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="phone"
              maxLength={10}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              onBlur={(e) => checkFieldDuplicate('phone', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? `bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 ${phoneDuplicateInfo ? 'border-yellow-400' : duplicateErrors.phone ? 'border-red-500' : 'focus:ring-green-500'}` 
                  : `bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 ${phoneDuplicateInfo ? 'border-yellow-400' : duplicateErrors.phone ? 'border-red-500' : 'focus:ring-green-500 focus:border-green-500'}`
              }`}
            />
            {phoneDuplicateInfo && (
              <div className="flex items-start gap-1 mt-1">
                <svg className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="text-yellow-700 text-xs">{phoneDuplicateInfo}</span>
              </div>
            )}
            {duplicateErrors.phone && (
              <span className="text-red-500 text-xs mt-1">{duplicateErrors.phone}</span>
            )}
          </div>
          
          {/* secondaryPhone */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Phone (Secondary)
            </label>
            <input
              type="text"
              name="secondaryPhone"
              maxLength={10}
              value={form.secondaryPhone}
              onChange={(e) => setForm({ ...form, secondaryPhone: e.target.value })}
              placeholder="Enter alternate contact number"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
              }`}
            />
          </div>
          
          {/* email */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Email
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              onBlur={(e) => checkFieldDuplicate('email', e.target.value)}
              placeholder="Enter email address"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? `bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 ${duplicateErrors.email ? 'border-red-500' : 'focus:ring-green-500'}` 
                  : `bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 ${duplicateErrors.email ? 'border-red-500' : 'focus:ring-green-500 focus:border-green-500'}`
              }`}
            />
            {duplicateErrors.email && (
              <span className="text-red-500 text-xs mt-1">{duplicateErrors.email}</span>
            )}
          </div>
          
          {/* station - Using StationSelect component */}
          <StationSelect
            value={form.station}
            onChange={(stationId) => setForm({ ...form, station: stationId })}
            stations={stations}
            onCreateNew={() => {
              resetStationForm();
              setShowCreateStation(true);
            }}
            customSelectStyles={customSelectStyles(darkMode)}
            darkMode={darkMode}
            required={true}
          />
          
          {/* Master Account Selection via MasterSelection */}
          <div className="flex flex-col sm:col-span-2 lg:col-span-1">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Master Account
            </label>
            <div className="flex gap-2">
              <div
                onClick={() => setShowMasterSelection(true)}
                className={`flex-1 border rounded-lg p-2.5 cursor-pointer transition-all duration-300 flex items-center justify-between min-h-[42px] ${
                  form.ma.maId
                    ? darkMode 
                      ? 'bg-gray-600 border-green-500/50 text-white hover:border-green-400'
                      : 'bg-green-50 border-green-300 text-gray-900 hover:border-green-500'
                    : darkMode 
                      ? 'bg-gray-600 border-gray-500 text-gray-400 hover:border-teal-400'
                      : 'bg-white border-gray-300 text-gray-500 hover:border-teal-500'
                }`}
              >
                <span className="truncate text-sm">
                  {form.ma.maId && form.ma.maType ? (
                    <span className={`flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        darkMode ? 'bg-teal-900/50 text-teal-300' : 'bg-teal-100 text-teal-700'
                      }`}>
                        {form.ma.maType === 'MA' ? 'Master Account' : form.ma.maType}
                      </span>
                      <span>{form.ma.maName || 'Selected'}</span>
                    </span>
                  ) : (
                    'Click to select Dealer / Reference / Master Account...'
                  )}
                </span>
                <svg className={`w-4 h-4 flex-shrink-0 ml-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                </svg>
              </div>
              {form.ma.maId && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, ma: { maType: '', maId: '', maName: '' } })}
                  className={`px-2.5 py-2 rounded-lg text-sm transition-colors ${
                    darkMode ? 'bg-gray-600 text-gray-400 hover:bg-red-900/40 hover:text-red-400' : 'bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-500'
                  }`}
                  title="Clear Master Account"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {/* siteAddress */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Billing Address
            </label>
            <input
              type="text"
              name="siteAddress"
              value={form.siteAddress}
              onChange={(e) => setForm({ ...form, siteAddress: e.target.value })}
              placeholder="Enter billing address"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
              }`}
            />
          </div>
          
          {/* deliveryAddress */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Delivery Address
            </label>
            <input
              type="text"
              name="deliveryAddress"
              value={form.deliveryAddress}
              onChange={(e) => setForm({ ...form, deliveryAddress: e.target.value })}
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
              }`}
              placeholder="Optional delivery address if different from billing address"
            />
          </div>
          
          {/* pinCode */}
          <div className="flex flex-col">
            <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              Pin Code
            </label>
            <input
              type="text"
              name="pinCode"
              value={form.pinCode}
              onChange={(e) => setForm({ ...form, pinCode: e.target.value })}
              placeholder="Enter pin code"
              className={`border rounded-lg p-2.5 w-full transition-colors duration-300 ${
                darkMode 
                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
              }`}
            />
          </div>
          
          {/* GST Verification Button or GST/PAN Fields */}
          {!showGstPanFields ? (
            // Show verify buttons when fields are hidden
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  GST Verification
                </label>
                <button
                  type="button"
                  onClick={openGSTVerification}
                  className={`px-4 py-2.5 rounded-lg transition-all text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] ${
                    darkMode 
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white' 
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white'
                  }`}
                  title="Verify GST with Government Portal"
                >
                  <span>🔍</span>
                  <span>Verify GST Number</span>
                </button>
                <p className={`text-xs mt-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Verify GSTIN and auto-extract PAN
                </p>
              </div>
              
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  PAN Verification
                </label>
                <button
                  type="button"
                  onClick={() => setShowPANVerification(true)}
                  className={`px-4 py-2.5 rounded-lg transition-all text-sm flex items-center justify-center space-x-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] ${
                    darkMode 
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800 text-white' 
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white'
                  }`}
                  title="Verify PAN directly with Government Portal"
                >
                  <span>🔍</span>
                  <span>Verify PAN Number</span>
                </button>
                <p className={`text-xs mt-1 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Verify PAN number independently
                </p>
              </div>
            </div>
          ) : (
            // Show GST and PAN fields after verification
            <>
              {/* gstNo */}
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  GST No {isGstReadOnly && <span className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>(Verified from GST Portal) 🔒</span>}
                </label>
                <div className="flex space-x-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      name="gstNo"
                      value={form.gstNo}
                      onChange={handleGSTChange}
                      placeholder="Enter 15-digit GST number"
                      maxLength="15"
                      disabled={isGstReadOnly}
                      className={`border rounded p-2 w-full focus:outline-blue-500 pr-10 transition-colors duration-300 ${
                        isGstReadOnly 
                          ? darkMode 
                            ? 'bg-gray-700 text-gray-400 cursor-not-allowed border-gray-600' 
                            : 'bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300'
                          : darkMode
                            ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500'
                            : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500'
                      } ${duplicateErrors.gstNo ? 'border-red-500' : ''}`}
                    />
                    {form.gstNo && (
                      <button
                        type="button"
                        onClick={handleClearGST}
                        className={`absolute right-2 top-1/2 transform -translate-y-1/2 transition-colors duration-300 ${
                          darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                        }`}
                        title="Clear GST and PAN"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={openGSTVerification}
                    disabled={isGstReadOnly}
                    className={`px-3 py-2 rounded transition-colors text-sm whitespace-nowrap ${
                      isGstReadOnly 
                        ? darkMode 
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-400 text-white cursor-not-allowed'
                        : darkMode
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                    title={isGstReadOnly ? "GST already verified" : "Verify GST with Government Portal"}
                  >
                    {isGstReadOnly ? '✅ Verified' : '🔍 Verify GST'}
                  </button>
                </div>
                {duplicateErrors.gstNo && (
                  <span className="text-red-500 text-xs mt-1">{duplicateErrors.gstNo}</span>
                )}
              </div>
              
              {/* panNo */}
              <div className="flex flex-col">
                <label className={`font-semibold mb-1 transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  PAN No {isPanReadOnly && <span className={`text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>(Auto-extracted from GST) 🔒</span>}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    name="panNo"
                    value={form.panNo}
                    onChange={handlePANChange}
                    disabled={isPanReadOnly}
                    placeholder="Enter PAN or provide GST to auto-extract"
                    maxLength="10"
                    className={`border rounded p-2 w-full focus:outline-blue-500 transition-colors duration-300 ${
                      isPanReadOnly 
                        ? darkMode 
                          ? 'bg-gray-700 text-gray-400 cursor-not-allowed border-gray-600' 
                          : 'bg-gray-200 text-gray-600 cursor-not-allowed border-gray-300'
                        : darkMode
                          ? 'bg-gray-700 text-white border-gray-600 focus:border-blue-500'
                          : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500'
                    } ${duplicateErrors.panNo ? 'border-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPANVerification(true)}
                    disabled={isPanReadOnly}
                    className={`px-3 py-2 rounded transition-colors text-sm whitespace-nowrap ${
                      isPanReadOnly 
                        ? darkMode 
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-gray-400 text-white cursor-not-allowed'
                        : darkMode
                          ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                    title={isPanReadOnly ? "PAN already verified from GST" : "Verify PAN with Government Portal"}
                  >
                    {isPanReadOnly ? '✅ From GST' : '🔍 Verify PAN'}
                  </button>
                </div>
                {duplicateErrors.panNo && (
                  <span className="text-red-500 text-xs mt-1">{duplicateErrors.panNo}</span>
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6">
          <button
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
            className={`group relative bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{isEditing ? 'Updating...' : 'Adding...'}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isEditing ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  )}
                </svg>
                <span>{isEditing ? 'Update Customer' : 'Add Customer'}</span>
              </>
            )}
          </button>
          {isEditing && (
            <button
              onClick={resetForm}
              className={`px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg ${
                darkMode 
                  ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                  : 'bg-gray-400 hover:bg-gray-500 text-white'
              }`}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* -------------------------
          SEARCH + PAGE SIZE
         ------------------------- */}
      <div className={`mb-4 sm:mb-5 md:mb-6 p-3 sm:p-4 rounded-lg md:rounded-xl border transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-700 border-gray-600' 
          : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <input
            type="text"
            placeholder="Search by name, phone, or secondary phone..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className={`border rounded-lg p-2.5 w-full sm:w-2/3 md:w-1/2 transition-colors duration-300 ${
              darkMode 
                ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500' 
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-green-500 focus:border-green-500'
            }`}
          />
          <Select
            value={pageSizeOptions.find(option => option.value === pageSize)}
            onChange={(selectedOption) => {
              setPageSize(selectedOption.value);
              setCurrentPage(1);
            }}
            options={pageSizeOptions}
            styles={customSelectStyles(darkMode)}
            className="w-full sm:w-1/3 md:w-1/4"
            isSearchable={false}
          />
        </div>
      </div>
      
      {/* -------------------------
          CUSTOMER LIST (Desktop Table)
         ------------------------- */}
      <div className="overflow-x-auto hidden lg:block">
        <div className={`rounded-xl shadow-lg border overflow-hidden transition-colors duration-300 ${
          darkMode 
            ? 'bg-gray-700 border-gray-600' 
            : 'bg-white border-gray-200'
        }`}>
          <table className="w-full">
            <thead className={`border-b transition-colors duration-300 ${
              darkMode 
                ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600' 
                : 'bg-gradient-to-r from-green-50 to-teal-50 border-gray-200'
            }`}>
              <tr>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Customer ID</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Name</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Phone</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Station</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Master Account</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Created By</th>
                <th className={`px-6 py-4 text-left text-sm font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>Actions</th>
              </tr>
            </thead>
            <tbody className={`divide-y transition-colors duration-300 ${
              darkMode ? 'divide-gray-600' : 'divide-gray-200'
            }`}>
              {customers?.map((customer, index) => (
                <tr key={customer._id} className={`transition-colors duration-200 ${
                  darkMode 
                    ? `hover:bg-gray-600 ${index % 2 === 0 ? 'bg-gray-700' : 'bg-gray-700/50'}`
                    : `hover:bg-green-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`
                }`}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-green-400' : 'text-green-600'
                  }`}>{customer.customerCode}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>{customer.name}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{customer.phone}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>{customer.station?.name || 'N/A'}</td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {customer.ma?.maId ? (
                      <div>
                        <div className={`text-xs font-medium transition-colors duration-300 ${
                          darkMode ? 'text-green-400' : 'text-green-600'
                        }`}>{customer.ma.maType === 'MA' ? 'Master Account' : customer.ma.maType}</div>
                        <div>{customer.ma.maType === 'Dealer' ? 
                          customer.ma.maId.businessName : 
                          customer.ma.maId.name || 'N/A'}</div>
                      </div>
                    ) : 'N/A'}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <div>
                      <div className="font-medium">{customer.createdBy?.name || 'N/A'}</div>
                      {customer.company?.hasSeparateCRM === false && customer.company?.name && (
                        <div className={`text-xs transition-colors duration-300 ${
                          darkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>{customer.company.name}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEditing(customer)}
                        className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-3 py-2 rounded-lg hover:from-green-600 hover:to-teal-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md transform hover:scale-105"
                      >
                        <FaEdit className="text-xs" />
                        Edit
                      </button>
                      <button
                        onClick={() => viewCustomerDetails(customer)}
                        className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-3 py-2 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center gap-1 transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <FaEye className="text-xs" />
                        View
                      </button>
                    </div>                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* -------------------------
          CUSTOMER LIST (Tablet Layout)
         ------------------------- */}
      <div className="hidden md:block lg:hidden">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Station</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customers?.map((customer, index) => (
                <tr key={customer._id} className={`hover:bg-blue-50 transition-colors duration-200 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="px-4 py-3 text-sm font-medium text-blue-600">{customer.customerCode}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">
                      {customer.salutation ? `${customer.salutation} ` : ''}{customer.name}
                    </div>
                    {customer.email && (
                      <div className="text-xs text-gray-500 truncate" title={customer.email}>
                        {customer.email}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div>{customer.phone}</div>
                    {customer.secondaryPhone && (
                      <div className="text-xs text-gray-500">{customer.secondaryPhone}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {customer.station?.name || 'N/A'}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    <div className="font-medium">{customer.createdBy?.name || 'N/A'}</div>
                    {customer.company?.hasSeparateCRM === false && customer.company?.name && (
                      <div className="text-xs text-gray-500">{customer.company.name}</div>
                    )}
                  </td>                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => startEditing(customer)}
                        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center justify-center gap-1 text-xs transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <FaEdit />
                        Edit
                      </button>
                      <button
                        onClick={() => viewCustomerDetails(customer)}
                        className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2 py-1 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center justify-center gap-1 text-xs transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <FaEye />
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* -------------------------
          MOBILE CARDS
         ------------------------- */}
      <div className="block md:hidden mt-4 space-y-3">        {customers?.map((customer) => (
          <div
            key={customer._id}
            className={`border rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-200 ${
              darkMode 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1 min-w-0 pr-2">
                <div className={`font-semibold text-base sm:text-lg truncate ${
                  darkMode ? 'text-gray-100' : 'text-gray-800'
                }`}>
                  {customer.salutation ? `${customer.salutation} ` : ''}{customer.name}
                </div>
                <div className={`text-xs sm:text-sm font-medium ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}>
                  ID: {customer.customerCode}
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => startEditing(customer)}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2.5 sm:px-3 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <FaEdit className="text-xs sm:text-sm" />
                  <span className="hidden xs:inline">Edit</span>
                </button>
                <button
                  onClick={() => viewCustomerDetails(customer)}
                  className="bg-gradient-to-r from-gray-500 to-gray-600 text-white px-2.5 sm:px-3 py-2 rounded-lg hover:from-gray-600 hover:to-gray-700 flex items-center gap-1 text-xs sm:text-sm transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <FaEye className="text-xs sm:text-sm" />
                  <span className="hidden xs:inline">View</span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:gap-3 text-xs sm:text-sm">
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-blue-900/40 border border-blue-700/50' 
                  : 'bg-blue-50 border border-blue-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>Phone:</span>
                <div className={`font-medium break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{customer.phone}</div>
              </div>
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-purple-900/40 border border-purple-700/50' 
                  : 'bg-purple-50 border border-purple-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>Secondary:</span>
                <div className={`break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{customer.secondaryPhone || '-'}</div>
              </div>
              <div className={`col-span-2 p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-green-900/40 border border-green-700/50' 
                  : 'bg-green-50 border border-green-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-green-300' : 'text-green-700'}`}>Email:</span>
                <div className={`break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{customer.email || '-'}</div>
              </div>
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-amber-900/40 border border-amber-700/50' 
                  : 'bg-amber-50 border border-amber-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>Station:</span>
                <div className={`truncate ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{customer.station?.name || 'N/A'}</div>
              </div>
              <div className={`p-2.5 sm:p-3 rounded-lg transition-colors duration-200 ${
                darkMode 
                  ? 'bg-indigo-900/40 border border-indigo-700/50' 
                  : 'bg-indigo-50 border border-indigo-100'
              }`}>
                <span className={`font-medium block mb-1 ${darkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>Created By:</span>
                <div className={darkMode ? 'text-gray-100' : 'text-gray-800'}>
                  <div className="font-medium truncate">{customer.createdBy?.name || 'N/A'}</div>
                  {customer.company?.hasSeparateCRM === false && customer.company?.name && (
                    <div className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{customer.company.name}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* -------------------------
          PAGINATION CONTROLS
         ------------------------- */}
      <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 mt-6 sm:mt-8 p-3 sm:p-4 rounded-lg shadow-sm border transition-colors duration-200 ${
        darkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}>
        <div className={`text-xs sm:text-sm text-center sm:text-left ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
          <span className="font-medium">{Math.min(currentPage * pageSize, totalCustomers)}</span> of{' '}
          <span className="font-medium">{totalCustomers}</span> customers
        </div>
        
        <div className="flex items-center space-x-1 sm:space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={`inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
              darkMode
                ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 disabled:bg-gray-800'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 disabled:bg-gray-100'
            }`}
          >
            <FaChevronLeft className="mr-1 sm:mr-2 h-3 w-3" />
            <span className="hidden xs:inline">Previous</span>
            <span className="xs:hidden">Prev</span>
          </button>
          
          <div className="flex items-center space-x-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-all duration-200 ${
                    currentPage === pageNum
                      ? 'bg-gradient-to-r from-green-600 to-teal-600 text-white shadow-md transform scale-105'
                      : darkMode
                      ? 'text-gray-300 hover:bg-gray-700 border border-gray-600'
                      : 'text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => {
              if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
              }
            }}
            disabled={currentPage === totalPages || totalPages === 0}
            className={`inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
              darkMode
                ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 disabled:bg-gray-800'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 disabled:bg-gray-100'
            }`}
          >
            <span className="hidden xs:inline">Next</span>
            <FaChevronRight className="ml-1 sm:ml-2 h-3 w-3" />
          </button>
        </div>
      </div>

      {/* -------------------------
          VIEW CUSTOMER DETAILS MODAL - Enhanced Styling
         ------------------------- */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-3 sm:p-4">
          <div className={`rounded-xl w-full max-w-md sm:max-w-lg lg:max-w-2xl mx-auto relative shadow-2xl transform transition-all duration-300 ease-in-out max-h-[95vh] sm:max-h-[90vh] flex flex-col ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            {/* Modal header with gradient */}
            <div className="bg-gradient-to-r from-green-600 via-teal-600 to-blue-600 text-white px-4 sm:px-6 py-4 sm:py-5 rounded-t-xl flex justify-between items-center flex-shrink-0 shadow-lg">
              <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2 sm:gap-3">
                <span className="bg-white/20 backdrop-blur-sm rounded-full p-2 shadow-md">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </span>
                <span>Customer Details</span>
              </h2>
              <button
                className="text-white hover:bg-white/20 focus:outline-none transition-all rounded-full p-2 hover:rotate-90 duration-300"
                onClick={closeViewCustomerModal}
                aria-label="Close"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal content - Scrollable */}
            <div className={`overflow-y-auto flex-1 p-4 sm:p-6 space-y-4 ${
              darkMode ? 'bg-gray-800' : 'bg-gray-50'
            }`}>
              {/* Customer Name - Prominent Display */}
              <div className={`rounded-xl p-4 sm:p-5 text-center shadow-md ${
                darkMode 
                  ? 'bg-gradient-to-br from-gray-700 to-gray-800 border border-gray-600' 
                  : 'bg-gradient-to-br from-white to-gray-50 border border-gray-200'
              }`}>
                <p className={`text-xs sm:text-sm font-medium mb-1 ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>Customer Name</p>
                <h3 className={`text-xl sm:text-2xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {selectedCustomer.salutation ? `${selectedCustomer.salutation} ` : ''}{selectedCustomer.name}
                </h3>
                <p className={`text-xs sm:text-sm mt-2 font-medium ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}>ID: {selectedCustomer.customerCode}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Contact Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-purple-900/20 border-purple-500' 
                    : 'bg-purple-50 border-purple-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-purple-300' : 'text-purple-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Contact Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Primary Phone</p>
                      <p className={`text-sm sm:text-base font-medium flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        <svg className={`w-4 h-4 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {selectedCustomer.phone}
                      </p>
                    </div>
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Secondary Phone</p>
                      <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        {selectedCustomer.secondaryPhone || '-'}
                      </p>
                    </div>
                    {selectedCustomer.email && (
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email</p>
                        <p className={`text-sm sm:text-base font-medium flex items-center gap-2 break-all ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          <svg className={`w-4 h-4 flex-shrink-0 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {selectedCustomer.email}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-green-900/20 border-green-500' 
                    : 'bg-green-50 border-green-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-green-300' : 'text-green-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location Information
                  </h3>
                  <div className="space-y-3">
                    {selectedCustomer.siteAddress && (
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Billing Address</p>
                        <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedCustomer.siteAddress}
                        </p>
                      </div>
                    )}
                    {selectedCustomer.deliveryAddress && (
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Delivery Address</p>
                        <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedCustomer.deliveryAddress}
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Station</p>
                        <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedCustomer.station?.name || 'N/A'}
                        </p>
                      </div>
                      {selectedCustomer.pinCode && (
                        <div>
                          <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pin Code</p>
                          <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedCustomer.pinCode}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Master Account Information */}
                {selectedCustomer.ma?.maId && (
                  <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                    darkMode 
                      ? 'bg-teal-900/20 border-teal-500' 
                      : 'bg-teal-50 border-teal-500'
                  }`}>
                    <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                      darkMode ? 'text-teal-300' : 'text-teal-800'
                    }`}>
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      Master Account Information
                    </h3>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Master Account Type</p>
                          <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedCustomer.ma.maType === 'MA' ? 'Master Account' : selectedCustomer.ma.maType}</p>
                        </div>
                        <div>
                          <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{selectedCustomer.ma.maType === 'MA' ? 'Master Account' : selectedCustomer.ma.maType} Name</p>
                          <p className={`text-sm sm:text-base font-medium truncate ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                            {selectedCustomer.ma.maType === 'Dealer' ? 
                              selectedCustomer.ma.maId.businessName : 
                              selectedCustomer.ma.maId.name || 'N/A'}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Contact</p>
                        <p className={`text-sm sm:text-base font-medium ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          {selectedCustomer.ma.maType === 'Dealer' ? 
                            selectedCustomer.ma.maId.phone : 
                            selectedCustomer.ma.maId.phoneNumber || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Legal Information */}
                <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                  darkMode 
                    ? 'bg-amber-900/20 border-amber-500' 
                    : 'bg-amber-50 border-amber-500'
                }`}>
                  <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                    darkMode ? 'text-amber-300' : 'text-amber-800'
                  }`}>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Legal Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>GST Number</p>
                      <p className={`text-sm sm:text-base font-medium font-mono ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedCustomer.gstNo || '-'}</p>
                    </div>
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>PAN Number</p>
                      <p className={`text-sm sm:text-base font-medium font-mono ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>{selectedCustomer.panNo || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Information */}
              <div className={`rounded-xl p-4 sm:p-5 border-l-4 shadow-md transition-all hover:shadow-lg ${
                darkMode 
                  ? 'bg-indigo-900/20 border-indigo-500' 
                  : 'bg-indigo-50 border-indigo-500'
              }`}>
                <h3 className={`font-semibold mb-3 text-sm sm:text-base flex items-center gap-2 ${
                  darkMode ? 'text-indigo-300' : 'text-indigo-800'
                }`}>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  System Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Created By</p>
                    <p className={`text-sm sm:text-base font-medium flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      <svg className={`w-4 h-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {selectedCustomer.createdBy?.name || 'N/A'}
                    </p>
                  </div>
                  {selectedCustomer.company?.hasSeparateCRM === false && selectedCustomer.company?.name && (
                    <div>
                      <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Company</p>
                      <p className={`text-sm sm:text-base font-medium flex items-center gap-2 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                        <svg className={`w-4 h-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        {selectedCustomer.company.name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Modal footer */}
            <div className={`px-4 sm:px-6 py-4 rounded-b-xl flex justify-center gap-3 flex-shrink-0 border-t ${
              darkMode 
                ? 'bg-gray-700 border-gray-600' 
                : 'bg-gray-100 border-gray-200'
            }`}>
              <button
                onClick={closeViewCustomerModal}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105 ${
                  darkMode
                    ? 'bg-gray-600 text-white hover:bg-gray-500 focus:ring-gray-500'
                    : 'bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-400 border border-gray-300'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Station Creation Modal */}
      {showCreateStation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl w-full max-w-md mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="p-1 bg-green-600 text-white rounded">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                Create New Station
              </h3>
              <button
                onClick={() => {
                  setShowCreateStation(false);
                  resetStationForm();
                }}
                className={`text-xl font-bold ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}
              >
                ×
              </button>
            </div>

            {/* Form */}
            <div className="p-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Station Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={stationForm.name}
                  onChange={handleStationFormChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="e.g., MUMBAI CENTRAL STATION"
                  style={{ textTransform: 'uppercase' }}
                />
                <p className={`mt-1 text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Station name will be converted to uppercase</p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  State <span className="text-red-500">*</span>
                </label>
                <Select
                  name="state"
                  value={stationForm.state ? { value: stationForm.state, label: stationForm.state } : null}
                  onChange={(selectedOption) => {
                    handleStationFormChange({
                      target: {
                        name: 'state',
                        value: selectedOption ? selectedOption.value : ''
                      }
                    });
                  }}
                  options={INDIAN_STATES.map(state => ({ value: state, label: state }))}
                  styles={{
                    ...customSelectStyles(darkMode),
                    control: (provided, state) => ({
                      ...customSelectStyles(darkMode).control(provided, state),
                      border: state.isFocused ? '2px solid #10B981' : `1px solid ${darkMode ? '#6b7280' : '#D1D5DB'}`,
                      boxShadow: state.isFocused ? '0 0 0 1px #10B981' : 'none',
                      '&:hover': {
                        border: state.isFocused ? '2px solid #10B981' : `1px solid ${darkMode ? '#9ca3af' : '#9CA3AF'}`
                      }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected 
                        ? '#10B981' 
                        : state.isFocused 
                          ? darkMode ? '#374151' : '#ECFDF5'
                          : darkMode ? '#1f2937' : 'white',
                      color: state.isSelected ? 'white' : darkMode ? '#f3f4f6' : '#374151'
                    })
                  }}
                  placeholder="Select State"
                  isSearchable
                  isClearable
                />
              </div>
            </div>

            {/* Actions */}
            <div className={`flex gap-3 p-4 border-t ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
              <button
                onClick={handleCreateStation}
                disabled={isCreatingStation}
                className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium"
              >
                {isCreatingStation ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Station
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setShowCreateStation(false);
                  resetStationForm();
                }}
                disabled={isCreatingStation}
                className={`px-4 py-2 border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  darkMode 
                    ? 'border-gray-600 text-gray-200 hover:bg-gray-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GST Verification Modal */}
      <GSTVerificationModal
        isOpen={showGSTVerification}
        onClose={() => setShowGSTVerification(false)}
        onDataReceived={handleGSTVerificationData}
        initialGSTIN={form.gstNo}
      />

      {/* PAN Verification Modal */}
      <PANVerificationModal
        isOpen={showPANVerification}
        onClose={() => setShowPANVerification(false)}
        onDataReceived={handlePANVerificationData}
        initialPAN={form.panNo}
      />

      {/* MasterSelection Modal for Master Account (Dealer / Reference / Master Account) */}
      <MasterSelection
        isOpen={showMasterSelection}
        onClose={() => setShowMasterSelection(false)}
        onSelect={(item, entityType) => {
          // Map entity type to MA type
          const maTypeMap = { 'Dealer': 'Dealer', 'Reference': 'Reference', 'MA': 'MA' };
          const maType = maTypeMap[entityType] || entityType;
          
          // Get display name based on entity type
          let maName = '';
          if (entityType === 'Dealer') {
            maName = item.businessName || item.name || '';
          } else {
            maName = item.name || item.guestName || '';
          }
          
          setForm(prev => ({
            ...prev,
            ma: {
              maType: maType,
              maId: item._id,
              maName: maName
            }
          }));
        }}
        allowedTypes={['Dealer', 'Reference', 'MA']}
        defaultType="Dealer"
        title="Select Master Account"
      />

      {/* Phone Duplicate Confirmation Modal */}
      {showPhoneDuplicateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-yellow-50 border-b border-yellow-200 p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <svg className="w-12 h-12 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-yellow-800 mb-2">
                    Duplicate Phone Number Detected
                  </h3>
                  <p className="text-yellow-700">
                    The phone number <span className="font-semibold">{form.phone}</span> is already registered with {duplicatePhoneCustomers.length} customer{duplicatePhoneCustomers.length > 1 ? 's' : ''}. 
                    Please review the existing customer details below before proceeding.
                  </p>
                </div>
              </div>
            </div>

            {/* Customer List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {duplicatePhoneCustomers.map((customer, index) => (
                  <div key={customer.id} className="border border-gray-200 rounded-lg p-5 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800">
                          Customer #{index + 1}: {customer.name}
                        </h4>
                        {customer.updatedAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Last Updated: {new Date(customer.updatedAt).toLocaleString('en-IN', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric', 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500 bg-white px-3 py-1 rounded-full">
                        ID: {customer.id.slice(-8)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Primary Phone</p>
                          <p className="text-sm text-gray-800 font-semibold">{customer.phone}</p>
                        </div>
                      </div>

                      {customer.secondaryPhone && customer.secondaryPhone !== 'N/A' && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Secondary Phone</p>
                            <p className="text-sm text-gray-800">{customer.secondaryPhone}</p>
                          </div>
                        </div>
                      )}

                      {customer.email && customer.email !== 'N/A' && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Email</p>
                            <p className="text-sm text-gray-800">{customer.email}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-purple-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div>
                          <p className="text-xs text-gray-500 font-medium">Station</p>
                          <p className="text-sm text-gray-800">{customer.station}</p>
                          <p className="text-xs text-gray-500">{customer.state}</p>
                        </div>
                      </div>

                      {customer.siteAddress && customer.siteAddress !== 'N/A' && (
                        <div className="flex items-start md:col-span-2">
                          <svg className="w-5 h-5 text-orange-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">Site Address</p>
                            <p className="text-sm text-gray-800">{customer.siteAddress}</p>
                          </div>
                        </div>
                      )}

                      {customer.gstNo && customer.gstNo !== 'N/A' && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">GST No.</p>
                            <p className="text-sm text-gray-800 font-mono">{customer.gstNo}</p>
                          </div>
                        </div>
                      )}

                      {customer.panNo && customer.panNo !== 'N/A' && (
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-indigo-500 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                          </svg>
                          <div>
                            <p className="text-xs text-gray-500 font-medium">PAN No.</p>
                            <p className="text-sm text-gray-800 font-mono">{customer.panNo}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer with Actions */}
            <div className="border-t border-gray-200 p-6 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handlePhoneDuplicateCancel}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
                >
                  Cancel & Review
                </button>
                <button
                  onClick={handlePhoneDuplicateConfirm}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-md"
                >
                  Proceed Anyway
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">
                By proceeding, you acknowledge that this phone number is already associated with other customers.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Auto-save Notification */}
      {(draftManager.autoSaving || showNotification) && (
        <div className="fixed top-20 right-4 z-50 animate-fade-in">
          <div 
            className={`rounded-lg shadow-2xl px-4 py-3 min-w-[250px] border-l-4 cursor-pointer transition-transform hover:scale-105 ${
              draftManager.autoSaving 
                ? 'bg-blue-50 border-blue-500' 
                : 'bg-green-50 border-green-500'
            }`}
            onClick={() => !draftManager.autoSaving && setShowNotification(false)}
            title={!draftManager.autoSaving ? 'Click to dismiss' : ''}
          >
            <div className="flex items-center gap-3">
              <div className={`text-2xl ${draftManager.autoSaving ? 'animate-spin' : ''}`}>
                {draftManager.autoSaving ? '🔄' : '✅'}
              </div>
              <div className="flex-1">
                <p className={`font-semibold text-sm ${
                  draftManager.autoSaving 
                    ? 'text-blue-800' 
                    : 'text-green-800'
                }`}>
                  {draftManager.autoSaving ? 'Auto-saving...' : notificationMessage}
                </p>
                {draftManager.lastSaved && !draftManager.autoSaving && (
                  <p className="text-xs mt-0.5 text-gray-600">
                    {draftManager.lastSaved.toLocaleTimeString('en-IN')}
                  </p>
                )}
              </div>
              {!draftManager.autoSaving && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowNotification(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
